import classNames from "classnames"
import React, { useCallback, useState } from "react"

import SubMenuBlock, { SubMenuBlockProps } from "./SubMenuBlock"
import { trackSegmentEvent } from "../../../utils/analytics"
import { getUrlFromStoryblokLink } from "../../../utils/storyblok"

import Link from "@components/elements/Link"
import ChevronIcon from "@components/elements/V2/Icons/ChevronIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
export interface MainLink {
  label: string
  link?: string | Storyblok.Link
  queryParams?: string
  menuColumnBlocks?: SubMenuBlockProps[]
  hasSubMenu?: boolean
  subMenu?: SubMenu
}

export interface SubMenu {
  title: string
  content: Array<{
    label: string
    menuContent: SubMenuBlockProps[]
  }>
}

type Props = {
  mainLink: MainLink
  className?: string
  mode: "light" | "dark"
  scrolled: boolean
  isMenuActive: boolean
  handleClick: () => void
  handleHover?: () => void
  isSubMenuActive: boolean
  isMenuOpenOnMobile: boolean
}

const NavHeading = ({
  mainLink,
  className,
  mode,
  scrolled,
  isMenuActive,
  handleClick,
  handleHover,
  isSubMenuActive,
  isMenuOpenOnMobile,
}: Props) => {
  const [hoveredIndex, setHoveredIndex] = useState(0)

  const commonClasses = classNames(
    className,
    "hover:border-charcoal/10 border border-transparent rounded-12",
    {
      "px-gutter-xs md:px-gutter-md max-w-[330px]": isMenuOpenOnMobile,
    },
    {
      "text-black lg:text-white": mode === "dark" && !scrolled && !isMenuActive,
    },
    {
      "text-black": (mode === "light" && scrolled) || isMenuActive,
    }
  )

  const onClick = useCallback(() => {
    trackSegmentEvent("main_navigation_link", {
      anchorText: mainLink.label,
      path:
        typeof mainLink.link === "string"
          ? mainLink.link
          : getUrlFromStoryblokLink(mainLink.link as Storyblok.Link),
    })
    handleClick()
  }, [mainLink.label, mainLink.link, handleClick])

  return (
    <React.Fragment>
      {mainLink.link ? (
        <Link
          className={commonClasses}
          link={mainLink.link}
          queryParams={mainLink.queryParams}
          onClick={onClick}
          onMouseEnter={() => handleHover?.()}
          onFocus={() => handleHover?.()}
          data-test-id={`mainlink-${mainLink.label}`}
        >
          {mainLink.label}
        </Link>
      ) : (
        <button
          className={commonClasses}
          onClick={() => handleClick()}
          onMouseEnter={() => handleHover?.()}
          onFocus={() => handleHover?.()}
          data-test-id={`mainlink-${mainLink.label}`}
        >
          {mainLink.label}
        </button>
      )}

      {/* Only render the desktop popover if isDesktop === true */}
      {mainLink.menuColumnBlocks?.length || mainLink.subMenu?.content ? (
        <div
          className={classNames(
            "pointer-events-none absolute left-0 top-[52px] z-50 mt-[15px] h-[450px] max-h-0 w-full overflow-hidden px-gutter-lg opacity-0 transition-all duration-200 ease-in-out xl:px-gutter-xl",
            {
              "pointer-events-auto max-h-[800px] pb-24 opacity-100":
                isSubMenuActive,
            },
            {
              "bg-white shadow-[0_16px_16px_-8px] shadow-charcoal/20":
                scrolled || isMenuActive,
            }
          )}
          onMouseLeave={() => setHoveredIndex(0)}
        >
          <div
            className={
              "rounded-sm -ml-32 w-[calc(100%_+_64px)] px-gutter-xs py-20 transition-height md:px-gutter-md md:py-24 lg:px-32 lg:py-64"
            }
          >
            {mainLink.hasSubMenu && mainLink.subMenu && (
              <div className="grid grid-cols-12 md:gap-grid-md lg:gap-grid-lg xl:gap-grid-xl">
                <div className="col-span-3">
                  <Typography
                    color={Color.Charcoal}
                    font="grotesk"
                    weight="book"
                    size="subscript-sm"
                    className="mb-16 uppercase"
                    text={mainLink.subMenu.title}
                  />

                  {mainLink.subMenu.content.map((contentItem, index) => (
                    <div
                      key={`submenu-content-${index}`}
                      className="my-16 flex w-full items-center justify-between last:mb-0 hover:cursor-pointer"
                    >
                      <div
                        onMouseEnter={() => {
                          handleHover?.()
                          setHoveredIndex(index)
                        }}
                        className="pr-32"
                      >
                        <Typography
                          color={Color.Charcoal}
                          font="grotesk"
                          weight="medium"
                          size="body-lg"
                          text={contentItem.label}
                          className={classNames("pt-[3px]", {
                            "opacity-50": hoveredIndex !== index,
                          })}
                        />
                      </div>
                      <ChevronIcon
                        className={classNames("-rotate-90", {
                          "opacity-0": hoveredIndex !== index,
                        })}
                      />
                    </div>
                  ))}
                </div>
                <div className="col-span-9 grid grid-cols-4 md:gap-grid-md lg:grid-cols-12 lg:gap-grid-lg xl:gap-grid-xl">
                  {mainLink.subMenu.content[hoveredIndex]?.menuContent.map(
                    (subMenuBlock, subMenuBlockIndex) => (
                      <SubMenuBlock
                        key={`navigation-submenu-${subMenuBlockIndex}`}
                        {...subMenuBlock}
                      />
                    )
                  )}
                </div>
              </div>
            )}
            {mainLink.menuColumnBlocks && (
              <div
                className={classNames(
                  "grid lg:grid-cols-12 lg:gap-grid-lg xl:gap-grid-xl",
                  {
                    "translate-y-0 opacity-100": isSubMenuActive,
                    "translate-y-8 opacity-0": !isSubMenuActive,
                  }
                )}
              >
                {mainLink.menuColumnBlocks.map((subMenuBlock, index) => (
                  <SubMenuBlock
                    key={`navigation-submenu-${index}`}
                    {...subMenuBlock}
                    className="!col-span-3"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  )
}

export default NavHeading
